import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Switch, FormControlLabel,
  Typography, TextField, MenuItem, Alert, CircularProgress, Box, Skeleton, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { TaskNotificationSettings, taskApi, taskErrorMessage } from '../../services/taskService';
import { tokens } from '../../theme';
import { plural } from '../../utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
}

const IN_APP_FIELDS: { key: keyof TaskNotificationSettings; label: string }[] = [
  { key: 'inAppAssigned', label: 'Przydzielono mi zadanie' },
  { key: 'inAppDueChanged', label: 'Zmieniono termin zadania' },
  { key: 'inAppComment', label: 'Nowy komentarz' },
  { key: 'inAppMention', label: 'Ktoś mnie oznaczył' },
  { key: 'inAppDueSoon', label: 'Zbliża się termin' },
  { key: 'inAppOverdue', label: 'Termin przekroczony' },
  { key: 'inAppWorkflow', label: 'Weryfikacja, akceptacja i zwrot do poprawy' },
];

const EMAIL_FIELDS: { key: keyof TaskNotificationSettings; label: string }[] = [
  { key: 'emailAssigned', label: 'Przydzielono mi zadanie' },
  { key: 'emailDueSoon', label: 'Termin (zbliża się / przekroczony)' },
  { key: 'emailWorkflow', label: 'Weryfikacja, akceptacja i zwrot do poprawy' },
];

const REMINDER_HOURS = [1, 2, 4, 8, 12, 24, 48, 72];

/** Naglowek grupy ustawien z ikona. */
const GroupTitle: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{description}</Typography>
    </Box>
  </Stack>
);

/** Wiersz przelacznika: etykieta po lewej, przelacznik po prawej, cala linia klikalna. */
const SwitchRow: React.FC<{ label: string; checked: boolean; disabled?: boolean; onChange: (value: boolean) => void; first?: boolean }> = ({
  label, checked, disabled, onChange, first,
}) => (
  <FormControlLabel
    disabled={disabled}
    labelPlacement="start"
    control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
    label={label}
    sx={{
      mx: 0,
      py: 1.25,
      gap: 2,
      width: '100%',
      justifyContent: 'space-between',
      borderTop: first ? 'none' : `1px solid ${tokens.line}`,
      '& .MuiFormControlLabel-label': { fontSize: '0.9375rem', minWidth: 0 },
      '& .MuiSwitch-root': { flexShrink: 0 },
    }}
  />
);

export const TaskNotificationSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const [settings, setSettings] = useState<TaskNotificationSettings | null>(null);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setSaved(false);
    taskApi
      .getNotificationSettings()
      .then((r) => { setSettings(r.settings); setEmailAvailable(r.emailAvailable); })
      .catch((err) => setError(taskErrorMessage(err, 'Nie udało się wczytać ustawień. Zamknij okno i spróbuj ponownie.')))
      .finally(() => setLoading(false));
  }, [open]);

  const patch = (key: keyof TaskNotificationSettings, value: boolean | number) => {
    setSettings((s) => (s ? { ...s, [key]: value } : s));
    setSaved(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    try {
      const res = await taskApi.updateNotificationSettings(settings);
      setSettings(res.settings);
      setSaved(true);
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się zapisać ustawień. Spróbuj ponownie za chwilę.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="task-notifications-title">
      <DialogTitle id="task-notifications-title" sx={{ pr: 7 }}>
        Powiadomienia o zadaniach
        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, fontWeight: 500, mt: 0.25 }}>
          Wybierz, o czym chcesz wiedzieć. Ustawienia dotyczą tylko Twojego konta.
        </Typography>
        <IconButton aria-label="Zamknij" onClick={onClose} sx={{ position: 'absolute', right: 14, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack gap={1.5} aria-busy="true" aria-label="Ładowanie ustawień">
            <Skeleton width="45%" height={24} />
            {Array.from({ length: 6 }).map((_, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                <Skeleton width={`${50 + ((i * 13) % 30)}%`} height={20} />
                <Skeleton variant="rounded" width={46} height={28} sx={{ borderRadius: 14 }} />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack gap={0}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {saved && <Alert severity="success" sx={{ mb: 2 }}>Ustawienia zostały zapisane.</Alert>}

            <GroupTitle
              icon={<NotificationsNoneOutlinedIcon />}
              title="W aplikacji"
              description="Powiadomienia pod ikoną dzwonka na górnym pasku."
            />
            <Box>
              {IN_APP_FIELDS.map((f, i) => (
                <SwitchRow
                  key={f.key}
                  first={i === 0}
                  label={f.label}
                  checked={Boolean(settings?.[f.key])}
                  onChange={(v) => patch(f.key, v)}
                />
              ))}
            </Box>

            <TextField
              select label="Przypomnienie o terminie"
              value={settings?.dueSoonHours ?? 24}
              onChange={(e) => patch('dueSoonHours', parseInt(e.target.value, 10))}
              sx={{ maxWidth: { sm: 300 }, mt: 1.5 }}
              fullWidth
              helperText="Jak wcześnie przypomnieć o terminie."
            >
              {REMINDER_HOURS.map((h) => (
                <MenuItem key={h} value={h}>{h} {plural(h, 'godzinę', 'godziny', 'godzin')} przed terminem</MenuItem>
              ))}
            </TextField>

            <Box sx={{ borderTop: `1px solid ${tokens.line}`, mt: 3, pt: 2.5 }}>
              <GroupTitle
                icon={<MailOutlineIcon />}
                title="E-mail"
                description="Wiadomości na adres, którym logujesz się do Visitio."
              />
              {!emailAvailable && (
                <Alert severity="info" sx={{ my: 1 }}>
                  Wysyłka e-maili nie jest jeszcze skonfigurowana na tym serwerze, więc te
                  powiadomienia pozostają wyłączone.
                </Alert>
              )}
              {EMAIL_FIELDS.map((f, i) => (
                <SwitchRow
                  key={f.key}
                  first={i === 0}
                  label={f.label}
                  disabled={!emailAvailable}
                  checked={Boolean(settings?.[f.key])}
                  onChange={(v) => patch(f.key, v)}
                />
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Zamknij</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading || !settings}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Zapisz ustawienia
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskNotificationSettingsDialog;
